import React, { Component } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import _ from "lodash";
import Lottie from "react-lottie";
import {
	Button,
	DropdownInput,
	RadioButton,
	Popup,
	SVGIcon,
	ImageInput,
	ColourPicker,
} from "../../../components";
import { Text } from "../../../components/text";
import { siteConfigActions, typeActions } from "../../../webapi";
import { setAuthLocation } from "../../../actions";
import {
	COLOUR_BRANDING_ACTION,
	COLOUR_BRANDING_MAIN,
	COLOUR_DUSK_LIGHT,
} from "../../../js";
import { unauthedFunction } from "../../../session";
import {
	getDebugInitialState,
	getDebugStep,
	debugLog,
	debugError,
	handleDebugTemplateAutoSelect,
	handleDebugSiteAutoSelect,
	shouldRecalculateVisibleSteps,
} from "./debugSiteBuilderWizard";

/**
 * Default icon for templates without an icon specified
 */
const DEFAULT_TEMPLATE_ICON = "settings6";

/**
 * Wizard step definitions
 */
const WIZARD_STEPS = {
	TEMPLATE_SELECTION: "template",
	SITE_NAME: "siteName",
	FEATURE_PICKER: "featurePicker",
	BRANDING: "branding",
	APP_VIEWS: "appViews",
	USER_TYPES: "userTypes",
	// Maintenance extension steps (enabled when maintenanceRequest NOT in Hidden)
	MAINTENANCE_WORKFLOW: "maintenanceWorkflow",
	MAINTENANCE_JOB_TYPES: "maintenanceJobTypes",
	// Forms extension steps (enabled when maintenanceRequestForms NOT in Hidden)
	FORMS_WORKFLOW: "formsWorkflow",
	FORMS: "forms",
	CONFIRMATION: "confirmation",
	CREATING: "creating",
	SUCCESS: "success",
};

/**
 * Normalize app view items from API format to UI format
 * API provides: Id, Title, Type, Settings, Components
 * UI expects: id, name (lowercase)
 */
const normalizeAppViews = (items) =>
	(items || []).map((item) => ({
		...item,
		id: item.Id || item.id,
		name: item.Title || item.name || item.Name,
	}));

/**
 * Normalize user type items from API format to UI format
 * API provides: id, displayName, typeName, permissions, etc.
 * UI expects: id, name (lowercase)
 */
const normalizeUserTypes = (items) =>
	(items || []).map((item) => ({
		...item,
		id: item.id || item.Id,
		name: item.displayName || item.name || item.Name || item.typeName,
	}));

/**
 * Normalize job type items from API format to UI format
 * API provides: id, typeName, site
 * UI expects: id, name (lowercase)
 *
 * Note: DB schema uses 'typeName' as the display name field
 */
const normalizeJobTypes = (items) =>
	(items || []).map((item) => ({
		...item,
		id: item.id || item.Id,
		name: item.typeName || item.name || item.Name,
	}));

/**
 * Normalize form type items from API format to UI format
 * API provides: id, typeName, site
 * UI expects: id, name (lowercase)
 *
 * Note: DB schema uses 'typeName' as the display name field
 */
const normalizeFormTypes = (items) =>
	(items || []).map((item) => ({
		...item,
		id: item.id || item.Id,
		name: item.typeName || item.name || item.Name,
	}));

class SiteBuilderWizard extends Component {
	constructor(props) {
		super(props);

		const allSteps = [
			WIZARD_STEPS.TEMPLATE_SELECTION,
			WIZARD_STEPS.SITE_NAME,
			WIZARD_STEPS.FEATURE_PICKER,
			WIZARD_STEPS.BRANDING,
			WIZARD_STEPS.APP_VIEWS,
			WIZARD_STEPS.USER_TYPES,
			WIZARD_STEPS.MAINTENANCE_WORKFLOW,
			WIZARD_STEPS.MAINTENANCE_JOB_TYPES,
			WIZARD_STEPS.FORMS_WORKFLOW,
			WIZARD_STEPS.FORMS,
			WIZARD_STEPS.CONFIRMATION,
		];

		// Get debug state overrides (empty object if debug disabled)
		const debugState = getDebugInitialState(WIZARD_STEPS, allSteps);

		this.state = {
			// Current wizard step
			currentStep: WIZARD_STEPS.TEMPLATE_SELECTION,
			completedSteps: [],

			// Template selection (step 1)
			templateSource: null,
			selectedPremadeTemplate: null,
			selectedExistingSite: null,

			// Site details (step 2)
			siteName: "",

			// Available data
			existingSites: [],
			sitesLoading: true,
			templateConfig: null,

			// Org templates from server
			orgTemplates: [],
			orgTemplatesLoading: true,
			orgTemplatesError: null,
			orgTemplatesConfigured: false,

			// Step options - whether to copy and/or link each section
			copyFeaturePicker: true,
			linkFeaturePicker: false,
			copyBranding: true,
			brandingLogo: null,
			brandingColor: COLOUR_BRANDING_MAIN,
			copyAppViews: {},
			linkAppViews: {},
			copyUserTypes: {},
			linkUserTypes: {},
			copyMaintenance: {},
			linkMaintenance: {},
			// Maintenance workflow state (separated for individual control)
			copyStatuses: true,
			linkStatuses: false,
			copyHideSeen: true,
			linkHideSeen: false,
			// Forms extension state
			copyForms: {},
			linkForms: {},
			// Forms workflow state (separated for individual control)
			copyFormsStatuses: true,
			linkFormsStatuses: false,
			copyFormsHideSeen: true,
			linkFormsHideSeen: false,

			// Template data (fetched) - full content from source
			templateData: null, // Full template content object
			templateDataLoading: false,
			templateDataError: null,

			// Job input - FLAT config structure that will be submitted
			// Initialized when template loads, modified as user progresses through wizard
			jobInput: null,

			// Legacy arrays (populated from templateData for backwards compatibility)
			templateAppViews: [],
			templateUserTypes: [],
			templateJobTypes: [],
			templateForms: [],

			// Maintenance workflow data (from templateData.maintenanceWorkflow)
			templateWorkflow: {
				statuses: [],
				hideSeen: false,
			},

			// Forms workflow data (from templateData.formsWorkflow)
			templateFormsWorkflow: {
				statuses: [],
				hideSeen: false,
			},

			// Visible steps (dynamic based on template)
			visibleSteps: [WIZARD_STEPS.TEMPLATE_SELECTION, WIZARD_STEPS.SITE_NAME],

			// Creation state
			creationStep: 0,
			creationError: null,
			createdSiteId: null,

			// Job polling state
			jobId: null,
			jobStatus: null,
			jobSteps: [],
			jobOutput: null,
			jobError: null,
			pollInterval: null,

			// Lottie animation
			lottieAnimationPath:
				"https://pluss-prd-media.s3-ap-southeast-2.amazonaws.com/assets/config-lottie.json",
			lottieOptions: {
				animationData: null,
				loop: true,
				autoplay: true,
				rendererSettings: {
					preserveAspectRatio: "xMidYMid slice",
				},
			},

			// UI state
			showErrors: false,
			showCancelConfirmation: false,

			// Debug overrides (applied last to override defaults)
			...debugState,
		};
	}

	componentDidMount() {
		this.loadExistingSites();
		this.loadOrgTemplates();
		this.getLottieConfig();
		// Debug mode: Template data loading is triggered after auto-selection in loadOrgTemplates/loadExistingSites
	}

	/**
	 * Load template data for debug mode
	 * Triggers the same data loading that would happen when clicking Next from template selection
	 */
	loadDebugTemplateData = () => {
		const { templateSource, selectedPremadeTemplate, selectedExistingSite } =
			this.state;

		debugLog(
			"[Wizard] 🔧 Debug mode: Loading template data for step:",
			getDebugStep(),
		);

		if (templateSource === "premade" && selectedPremadeTemplate) {
			this.loadPremadeTemplateData(selectedPremadeTemplate);
		} else if (templateSource === "existing" && selectedExistingSite) {
			this.loadTemplateData(selectedExistingSite.Key);
		} else if (templateSource === "scratch") {
			// Scratch doesn't need template data, but we should recalculate visible steps
			this.setState({ visibleSteps: this.calculateVisibleSteps() });
		} else {
			debugLog("[Wizard] ⚠️ Debug mode: No valid template source configured");
		}
	};

	componentWillUnmount() {
		// Clean up polling interval
		if (this.state.pollInterval) {
			clearInterval(this.state.pollInterval);
		}
	}

	// =========================================================================
	// JOB INPUT MANAGEMENT
	// =========================================================================

	/**
	 * Initialize jobInput from template data
	 * This creates the FLAT config structure that will be submitted to the backend
	 * @param {object} templateData - The fetched template data (FLAT format)
	 * @param {object} source - The source reference { type: "template"|"site", id: string } or null
	 */
	initializeJobInput = (templateData, source) => {
		const { siteName } = this.state;

		// DEBUG: Log incoming template data to trace widget loss
		const appViewsItems =
			(templateData && templateData.appViews && templateData.appViews.items) ||
			[];
		debugLog("[Wizard] 📥 initializeJobInput - templateData.appViews:", {
			hasAppViews: !!(templateData && templateData.appViews),
			itemCount: appViewsItems.length,
			items: appViewsItems.map((v) => ({
				Id: v.Id,
				isDefault: v.isDefault,
				hasTabs: !!v.tabs,
				tabCount: (v.tabs && v.tabs.length) || 0,
				tabs: (v.tabs || []).map((t) => ({
					key: t.key,
					type: t.type,
					widgetsCount: (t.widgets || []).length,
					widgets: t.widgets || [],
				})),
			})),
		});

		// Extract arrays from template data (FLAT format)
		const userTypeItems =
			(templateData &&
				templateData.userTypes &&
				templateData.userTypes.items) ||
			[];
		const appViewItems =
			(templateData && templateData.appViews && templateData.appViews.items) ||
			[];
		const jobTypeItems =
			(templateData &&
				templateData.maintenanceJobTypes &&
				templateData.maintenanceJobTypes.items) ||
			[];
		const formTypeItems =
			(templateData &&
				templateData.formsJobTypes &&
				templateData.formsJobTypes.items) ||
			[];

		const jobInput = {
			// === IDENTITY ===
			name: siteName || "",
			description: null,
			icon: null,

			// === RELATIONSHIPS ===
			source: source,
			extends: null, // Will be set based on linking choices
			siteId: null, // Will be set by backend after site creation

			// === CONFIG SECTIONS ===
			branding: {
				_mode: "local",
				logo:
					(templateData &&
						templateData.branding &&
						templateData.branding.logo) ||
					null,
				color:
					(templateData &&
						templateData.branding &&
						templateData.branding.color) ||
					COLOUR_BRANDING_MAIN,
			},

			featurePicker: {
				_mode: "local",
				Hidden:
					(templateData &&
						templateData.featurePicker &&
						templateData.featurePicker.Hidden) ||
					[],
				NotHidden:
					(templateData &&
						templateData.featurePicker &&
						templateData.featurePicker.NotHidden) ||
					[],
				Settings:
					(templateData &&
						templateData.featurePicker &&
						templateData.featurePicker.Settings) ||
					{},
			},

			userTypes: {
				_mode: "local",
				items: userTypeItems.map((ut) => ({
					...ut,
					_mode: "local",
					_selected: true, // UI-only, removed on submission
				})),
			},

			appViews: {
				_mode: "local",
				items: appViewItems.map((v) => ({
					...v,
					_mode: "local",
					_selected: true,
				})),
			},

			// === FLAT FORMAT (matches backend siteconfigs table) ===
			maintenanceJobTypes: {
				_mode: "local",
				items: jobTypeItems.map((jt) => ({
					...jt,
					_mode: "local",
					_selected: true,
				})),
			},

			maintenanceWorkflow: {
				_mode: "local",
				...((templateData && templateData.maintenanceWorkflow) || {}),
			},

			formsJobTypes: {
				_mode: "local",
				items: formTypeItems.map((ft) => ({
					...ft,
					_mode: "local",
					_selected: true,
				})),
			},

			formsWorkflow: {
				_mode: "local",
				...((templateData && templateData.formsWorkflow) || {}),
			},
		};

		debugLog("[Wizard] 🎯 initializeJobInput:", jobInput);
		return jobInput;
	};

	/**
	 * Update a top-level property in jobInput
	 * @param {string} key - The property name
	 * @param {any} value - The new value
	 */
	updateJobInput = (key, value) => {
		this.setState((prevState) => ({
			jobInput: {
				...prevState.jobInput,
				[key]: value,
			},
		}));
	};

	/**
	 * Update a section in jobInput (branding, featurePicker, userTypes, etc.)
	 * @param {string} section - The section name
	 * @param {object} updates - Properties to merge into the section
	 */
	updateJobInputSection = (section, updates) => {
		this.setState((prevState) => ({
			jobInput: {
				...prevState.jobInput,
				[section]: {
					...prevState.jobInput[section],
					...updates,
				},
			},
		}));
	};

	/**
	 * Update an item in an array within a section
	 * @param {string} section - The section name (userTypes, appViews, maintenance, forms)
	 * @param {string} arrayKey - The array key within the section (items, jobTypes, formTypes)
	 * @param {string} itemId - The ID of the item to update
	 * @param {object} updates - Properties to merge into the item
	 */
	updateJobInputArrayItem = (section, arrayKey, itemId, updates) => {
		this.setState((prevState) => {
			const sectionData = prevState.jobInput[section];
			const items = sectionData[arrayKey] || [];
			const updatedItems = items.map((item) => {
				const id = item.id || item.Id;
				if (id === itemId) {
					return { ...item, ...updates };
				}
				return item;
			});

			return {
				jobInput: {
					...prevState.jobInput,
					[section]: {
						...sectionData,
						[arrayKey]: updatedItems,
					},
				},
			};
		});
	};

	/**
	 * Prepare jobInput for submission by removing UI-only properties
	 * @returns {object} - Clean jobInput ready for API submission
	 */
	prepareJobInputForSubmission = () => {
		const {
			jobInput,
			templateSource,
			linkUserTypes,
			linkAppViews,
			linkMaintenance,
			linkForms,
			linkStatuses,
			linkHideSeen,
			linkFormsStatuses,
			linkFormsHideSeen,
			// Copy flags - these determine what gets included
			copyMaintenance,
			copyForms,
			copyStatuses,
			copyHideSeen,
			copyFormsStatuses,
			copyFormsHideSeen,
		} = this.state;
		if (!jobInput) return null;

		// Deep clone to avoid mutating state
		const submission = JSON.parse(JSON.stringify(jobInput));

		// Filter out unselected items, remove _selected property, and apply link state
		// For userTypes and appViews: use _selected property (updated via handlers)
		// For job types: use copyState object (UI updates these, not _selected)
		const cleanAndApplyLinkState = (items, linkState, copyState = null) => {
			return items
				.filter((item) => {
					const itemId = item.id || item.Id;
					// If copyState is provided, use it; otherwise fall back to _selected
					if (copyState !== null) {
						return copyState[itemId] !== false;
					}
					return item._selected !== false;
				})
				.map((item) => {
					const { _selected, ...rest } = item;
					const itemId = item.id || item.Id;
					// Apply link state from component state if it was set
					if (linkState && linkState[itemId] !== undefined) {
						rest._mode = linkState[itemId] ? "linked" : "local";
					}
					return rest;
				});
		};

		submission.userTypes.items = cleanAndApplyLinkState(
			submission.userTypes.items,
			linkUserTypes,
		);
		submission.appViews.items = cleanAndApplyLinkState(
			submission.appViews.items,
			linkAppViews,
		);
		// Job types use copyMaintenance/copyForms state objects (UI doesn't update _selected)
		submission.maintenanceJobTypes.items = cleanAndApplyLinkState(
			submission.maintenanceJobTypes.items,
			linkMaintenance,
			copyMaintenance,
		);
		submission.formsJobTypes.items = cleanAndApplyLinkState(
			submission.formsJobTypes.items,
			linkForms,
			copyForms,
		);

		// Compute category-level _mode based on item-level modes
		// Category mode should be "linked" if ANY item in the category is linked
		// This ensures the backend propagation logic works correctly
		const hasSomeLinked = (items) =>
			items.some((item) => item._mode === "linked");

		// Set category-level modes based on items
		if (hasSomeLinked(submission.userTypes.items)) {
			submission.userTypes._mode = "linked";
		}
		if (hasSomeLinked(submission.appViews.items)) {
			submission.appViews._mode = "linked";
		}
		if (hasSomeLinked(submission.maintenanceJobTypes.items)) {
			submission.maintenanceJobTypes._mode = "linked";
		}
		if (hasSomeLinked(submission.formsJobTypes.items)) {
			submission.formsJobTypes._mode = "linked";
		}

		// Apply workflow copy and link states (statuses and hideSeen)
		// If user chose not to copy, set to null so backend uses defaults (not sourceData)
		// Note: undefined would fall back to sourceData in backend merge, null signals "use defaults"
		if (submission.maintenanceWorkflow) {
			// Set statuses to null if not copying (signals "use defaults")
			if (!copyStatuses) {
				submission.maintenanceWorkflow.statuses = null;
			} else if (linkStatuses) {
				submission.maintenanceWorkflow._mode = "linked";
			}
			// Set hideSeen to null if not copying (signals "use defaults")
			if (!copyHideSeen) {
				submission.maintenanceWorkflow.hideSeen = null;
			} else if (linkHideSeen) {
				// If linking hideSeen, ensure workflow mode is linked
				submission.maintenanceWorkflow._mode = "linked";
			}
		}
		if (submission.formsWorkflow) {
			// Set statuses to null if not copying (signals "use defaults")
			if (!copyFormsStatuses) {
				submission.formsWorkflow.statuses = null;
			} else if (linkFormsStatuses) {
				submission.formsWorkflow._mode = "linked";
			}
			// Set hideSeen to null if not copying (signals "use defaults")
			if (!copyFormsHideSeen) {
				submission.formsWorkflow.hideSeen = null;
			} else if (linkFormsHideSeen) {
				// If linking hideSeen, ensure workflow mode is linked
				submission.formsWorkflow._mode = "linked";
			}
		}

		// Set extends based on linking (only for existing sites with any linked items)
		if (templateSource === "existing") {
			const hasLinking =
				submission.branding._mode === "linked" ||
				submission.featurePicker._mode === "linked" ||
				submission.userTypes._mode === "linked" ||
				submission.appViews._mode === "linked" ||
				submission.maintenanceJobTypes._mode === "linked" ||
				submission.maintenanceWorkflow._mode === "linked" ||
				submission.formsJobTypes._mode === "linked" ||
				submission.formsWorkflow._mode === "linked";

			submission.extends = hasLinking
				? submission.source && submission.source.id
				: null;
		}

		// DEBUG: Log specific appViews structure to trace widget loss
		const submissionItems =
			(submission.appViews && submission.appViews.items) || [];
		debugLog("[Wizard] 📤 prepareJobInputForSubmission - appViews:", {
			itemCount: submissionItems.length,
			items: submissionItems.map((v) => ({
				Id: v.Id,
				isDefault: v.isDefault,
				hasTabs: !!v.tabs,
				tabCount: (v.tabs && v.tabs.length) || 0,
				tabs: (v.tabs || []).map((t) => ({
					key: t.key,
					type: t.type,
					widgetsCount: (t.widgets || []).length,
					widgets: t.widgets || [],
				})),
			})),
		});
		debugLog("[Wizard] 📤 prepareJobInputForSubmission - full:", submission);
		return submission;
	};

	// =========================================================================
	// DATA LOADING
	// =========================================================================

	getLottieConfig = () => {
		unauthedFunction({
			method: "GET",
			url: this.state.lottieAnimationPath,
		})
			.then((res) => {
				this.setState((prevState) => ({
					lottieOptions: {
						...prevState.lottieOptions,
						animationData: res.data,
					},
				}));
			})
			.catch(() => {
				// Lottie animation is optional - ignore errors
			});
	};

	loadExistingSites = async () => {
		try {
			this.setState({ sitesLoading: true });
			const { data } = await typeActions.getSites();
			const sites = (data || [])
				.map((site) => ({
					Key: site.Id,
					Title: site.siteName,
					site: site,
				}))
				.sort((a, b) => (a.Title || "").localeCompare(b.Title || ""));
			this.setState(
				{
					existingSites: sites,
					sitesLoading: false,
				},
				() => {
					// Debug mode: Auto-select site and load data if needed
					handleDebugSiteAutoSelect(
						sites,
						this.setState.bind(this),
						this.loadDebugTemplateData,
						WIZARD_STEPS,
					);
				},
			);
		} catch (error) {
			console.error("Failed to load sites:", error);
			this.setState({ sitesLoading: false, existingSites: [] });
		}
	};

	loadOrgTemplates = async () => {
		try {
			this.setState({ orgTemplatesLoading: true, orgTemplatesError: null });
			const { data } = await siteConfigActions.getOrgTemplates();

			if (data.configured && data.templates && data.templates.length > 0) {
				// Transform templates to the format expected by the UI
				// Backend returns: id, name, description, icon (SVG icon name)
				const templates = data.templates.map((template) => ({
					key: template.id,
					title: template.name,
					icon: template.icon || DEFAULT_TEMPLATE_ICON,
					description: template.description || "",
					templateId: template.id,
				}));

				this.setState(
					{
						orgTemplates: templates,
						orgTemplatesLoading: false,
						orgTemplatesConfigured: true,
					},
					() => {
						// Debug mode: Auto-select template and load data if needed
						handleDebugTemplateAutoSelect(
							templates,
							this.setState.bind(this),
							this.loadDebugTemplateData,
							WIZARD_STEPS,
						);
					},
				);
			} else {
				// No templates available or not configured
				this.setState({
					orgTemplates: [],
					orgTemplatesLoading: false,
					orgTemplatesConfigured: data.configured || false,
					orgTemplatesError: data.configured
						? null
						: "Pre-made templates are not configured for this account",
				});
			}
		} catch (error) {
			console.error("Failed to load org templates:", error);
			this.setState({
				orgTemplates: [],
				orgTemplatesLoading: false,
				orgTemplatesError: "Unable to load pre-made templates",
			});
		}
	};

	// Load template data when an existing site is selected
	loadTemplateData = async (siteId) => {
		debugLog("[Wizard] 🔵 loadTemplateData - siteId:", siteId);
		this.setState({ templateDataLoading: true, templateDataError: null });

		try {
			// Fetch site configuration as template from backend (single API call)
			// Backend returns FLAT config format (matches siteconfigs table structure)
			const { data } = await siteConfigActions.getTemplateFromSite(siteId);
			debugLog("[Wizard] 📦 loadTemplateData - API response:", data);

			// DEBUG: Log appViews from API response to trace widget loss
			const apiAppViews =
				(data &&
					data.template &&
					data.template.appViews &&
					data.template.appViews.items) ||
				[];
			const generalView = apiAppViews.find(
				(v) => v.isDefault || v.Id === "general",
			);
			debugLog("[Wizard] 📦 API Response - appViews structure:", {
				hasAppViews: !!(data && data.template && data.template.appViews),
				itemCount: apiAppViews.length,
				generalView: generalView,
				generalViewTabs:
					generalView && generalView.tabs
						? generalView.tabs.map((t) => ({
								key: t.key,
								type: t.type,
								widgetsCount: (t.widgets || []).length,
								widgets: t.widgets,
							}))
						: [],
			});

			if (!data.success || !data.template) {
				throw new Error(data.error || "Failed to load site configuration");
			}

			// Store the FLAT config directly
			const templateData = data.template;
			const source = { type: "site", id: siteId };

			// Extract and normalize arrays from FLAT format (matches backend siteconfigs table)
			const appViewItems = normalizeAppViews(
				templateData.appViews && templateData.appViews.items,
			);
			const userTypeItems = normalizeUserTypes(
				templateData.userTypes && templateData.userTypes.items,
			);
			const jobTypeItems = normalizeJobTypes(
				templateData.maintenanceJobTypes &&
					templateData.maintenanceJobTypes.items,
			);
			const formTypeItems = normalizeFormTypes(
				templateData.formsJobTypes && templateData.formsJobTypes.items,
			);

			// Extract workflow settings from FLAT format
			const workflowData = templateData.maintenanceWorkflow || {};

			// Extract workflow settings from FLAT format
			const formsWorkflowData = templateData.formsWorkflow || {};

			// Initialize jobInput from template data
			const jobInput = this.initializeJobInput(templateData, source);

			// Update state with template data and jobInput
			this.setState(
				{
					templateData: templateData,
					templateDataLoading: false,
					jobInput: jobInput,

					// Pre-populate branding fields from template (for UI components)
					brandingLogo: jobInput.branding.logo,
					brandingColor: jobInput.branding.color,

					// Arrays for wizard step display
					templateAppViews: appViewItems,
					templateUserTypes: userTypeItems,
					templateJobTypes: jobTypeItems,
					templateForms: formTypeItems,

					// Workflow data for maintenance workflow step
					templateWorkflow: {
						statuses: workflowData.statuses || [],
						hideSeen: workflowData.hideSeen || false,
					},

					// Workflow data for forms workflow step
					templateFormsWorkflow: {
						statuses: formsWorkflowData.statuses || [],
						hideSeen: formsWorkflowData.hideSeen || false,
					},
				},
				() => {
					// Debug mode: Recalculate visible steps after data loads
					if (shouldRecalculateVisibleSteps(WIZARD_STEPS)) {
						const newVisibleSteps = this.calculateVisibleSteps();
						debugLog(
							"[Wizard] 🔧 Debug mode: Recalculated visibleSteps:",
							newVisibleSteps,
						);
						this.setState({ visibleSteps: newVisibleSteps });
					}
				},
			);
			debugLog("[Wizard] 🟢 loadTemplateData - complete, jobInput initialized");
		} catch (error) {
			debugError("[Wizard] ❌ loadTemplateData - error:", error);
			this.setState({
				templateDataLoading: false,
				templateDataError: "Failed to load template data. Please try again.",
			});
		}
	};

	// Load pre-made template data from Org account
	loadPremadeTemplateData = async (template) => {
		this.setState({ templateDataLoading: true, templateDataError: null });

		try {
			// Fetch full template content from backend
			// Backend returns FLAT config format (matches siteconfigs table structure)
			const templateId = template.id || template.templateId || template.key;
			debugLog(
				"[Wizard] 📡 loadPremadeTemplateData - fetching templateId:",
				templateId,
			);
			const { data } = await siteConfigActions.getTemplateFromOrg(templateId);
			debugLog("[Wizard] 📦 loadPremadeTemplateData - API response:", data);

			if (!data.success || !data.template) {
				throw new Error(data.error || "Failed to load template content");
			}

			// Store the FLAT config directly
			const templateData = data.template;
			const source = { type: "template", id: templateId };

			// Extract and normalize arrays from FLAT format (matches backend siteconfigs table)
			const appViewItems = normalizeAppViews(
				templateData.appViews && templateData.appViews.items,
			);
			const userTypeItems = normalizeUserTypes(
				templateData.userTypes && templateData.userTypes.items,
			);
			const jobTypeItems = normalizeJobTypes(
				templateData.maintenanceJobTypes &&
					templateData.maintenanceJobTypes.items,
			);
			const formTypeItems = normalizeFormTypes(
				templateData.formsJobTypes && templateData.formsJobTypes.items,
			);

			// Extract workflow settings from FLAT format
			const workflowData = templateData.maintenanceWorkflow || {};

			// Extract workflow settings from FLAT format
			const formsWorkflowData = templateData.formsWorkflow || {};

			// Initialize jobInput from template data
			const jobInput = this.initializeJobInput(templateData, source);

			// Update state with template data and jobInput
			this.setState(
				{
					templateData: templateData,
					templateDataLoading: false,
					jobInput: jobInput,

					// Pre-populate branding fields from template (for UI components)
					brandingLogo: jobInput.branding.logo,
					brandingColor: jobInput.branding.color,

					// Arrays for wizard step display
					templateAppViews: appViewItems,
					templateUserTypes: userTypeItems,
					templateJobTypes: jobTypeItems,
					templateForms: formTypeItems,

					// Workflow data for maintenance workflow step
					templateWorkflow: {
						statuses: workflowData.statuses || [],
						hideSeen: workflowData.hideSeen || false,
					},

					// Workflow data for forms workflow step
					templateFormsWorkflow: {
						statuses: formsWorkflowData.statuses || [],
						hideSeen: formsWorkflowData.hideSeen || false,
					},
				},
				() => {
					// Debug mode: Recalculate visible steps after data loads
					if (shouldRecalculateVisibleSteps(WIZARD_STEPS)) {
						const newVisibleSteps = this.calculateVisibleSteps();
						debugLog(
							"[Wizard] 🔧 Debug mode: Recalculated visibleSteps:",
							newVisibleSteps,
						);
						this.setState({ visibleSteps: newVisibleSteps });
					}
				},
			);
			debugLog(
				"[Wizard] 🟢 loadPremadeTemplateData - complete, jobInput initialized",
			);
		} catch (error) {
			debugError("[Wizard] ❌ loadPremadeTemplateData - error:", error);
			this.setState({
				templateDataLoading: false,
				templateDataError: "Failed to load template data. Please try again.",
			});
		}
	};

	// Always show maintenance steps (users should be able to configure job types for any site)
	isMaintenanceEnabled = () => {
		return true;
	};

	// Always show forms steps (users should be able to configure form types for any site)
	isFormsEnabled = () => {
		return true;
	};

	// Check if app views exist in the template
	hasAppViews = () => {
		const { templateAppViews } = this.state;
		return templateAppViews && templateAppViews.length > 0;
	};

	// Calculate which steps should be visible based on template content
	calculateVisibleSteps = () => {
		const {
			templateSource,
			templateAppViews,
			templateJobTypes,
			templateForms,
		} = this.state;

		debugLog("[Wizard] 📊 calculateVisibleSteps - state check:", {
			templateSource,
			templateAppViews: (templateAppViews && templateAppViews.length) || 0,
			templateJobTypes: (templateJobTypes && templateJobTypes.length) || 0,
			templateForms: (templateForms && templateForms.length) || 0,
			hasAppViews: this.hasAppViews(),
			isMaintenanceEnabled: this.isMaintenanceEnabled(),
			isFormsEnabled: this.isFormsEnabled(),
		});

		const steps = [WIZARD_STEPS.TEMPLATE_SELECTION, WIZARD_STEPS.SITE_NAME];

		// If scratch, we only need template selection and site name
		if (templateSource === "scratch") {
			return steps;
		}

		// Always show feature picker and branding
		steps.push(WIZARD_STEPS.FEATURE_PICKER);
		steps.push(WIZARD_STEPS.BRANDING);

		// App Views step shows when the template/site has app views defined
		if (this.hasAppViews()) {
			steps.push(WIZARD_STEPS.APP_VIEWS);
		}

		// Always show user types (most sites have user types)
		steps.push(WIZARD_STEPS.USER_TYPES);

		// Maintenance extension steps (when maintenanceRequest NOT in Hidden)
		if (this.isMaintenanceEnabled()) {
			steps.push(WIZARD_STEPS.MAINTENANCE_WORKFLOW);
			steps.push(WIZARD_STEPS.MAINTENANCE_JOB_TYPES);
		}

		// Forms extension steps (when maintenanceRequestForms NOT in Hidden)
		if (this.isFormsEnabled()) {
			steps.push(WIZARD_STEPS.FORMS_WORKFLOW);
			steps.push(WIZARD_STEPS.FORMS);
		}

		steps.push(WIZARD_STEPS.CONFIRMATION);

		return steps;
	};

	/**
	 * Mapping between wizard steps and template configuration keys
	 * Used to extract default values from template
	 */
	getTemplateConfigMapping = () => ({
		[WIZARD_STEPS.FEATURE_PICKER]: "featurePicker",
		[WIZARD_STEPS.BRANDING]: "branding",
		[WIZARD_STEPS.APP_VIEWS]: "appViews",
		[WIZARD_STEPS.USER_TYPES]: "userTypes",
		[WIZARD_STEPS.MAINTENANCE_WORKFLOW]: "maintenanceWorkflow",
		[WIZARD_STEPS.MAINTENANCE_JOB_TYPES]: "maintenanceJobTypes",
		[WIZARD_STEPS.FORMS_WORKFLOW]: "formsWorkflow",
		[WIZARD_STEPS.FORMS]: "formsJobTypes",
	});

	// Navigation
	goToStep = (step) => {
		// Only allow going back to completed steps
		if (this.state.completedSteps.includes(step)) {
			this.setState({ currentStep: step, showErrors: false });
		}
	};

	nextStep = () => {
		const {
			currentStep,
			templateData,
			templateSource,
			selectedPremadeTemplate,
			selectedExistingSite,
		} = this.state;
		debugLog("[Wizard] ➡️ nextStep - from:", currentStep);
		debugLog("[Wizard] 📊 nextStep - templateData:", templateData);

		if (!this.validateCurrentStep()) {
			this.setState({ showErrors: true });
			return;
		}

		const { visibleSteps, completedSteps } = this.state;

		// Handle special case: site name step with "scratch" option
		if (
			currentStep === WIZARD_STEPS.SITE_NAME &&
			templateSource === "scratch"
		) {
			// Navigate to existing Feature Picker flow
			this.props.history.push("/newsite");
			return;
		}

		// Load template data when leaving template selection (not scratch)
		// This starts loading in the background while user enters site name
		if (
			currentStep === WIZARD_STEPS.TEMPLATE_SELECTION &&
			templateSource !== "scratch"
		) {
			debugLog("[Wizard] 📡 nextStep - triggering template data load");
			if (templateSource === "premade" && selectedPremadeTemplate) {
				this.loadPremadeTemplateData(selectedPremadeTemplate);
			} else if (templateSource === "existing" && selectedExistingSite) {
				this.loadTemplateData(selectedExistingSite.Key);
			}
		}

		// Mark current step as completed
		const newCompletedSteps = [...completedSteps];
		if (!newCompletedSteps.includes(currentStep)) {
			newCompletedSteps.push(currentStep);
		}

		// If moving from site name step, calculate visible steps first
		if (currentStep === WIZARD_STEPS.SITE_NAME) {
			this.setState({ completedSteps: newCompletedSteps }, () => {
				const newVisibleSteps = this.calculateVisibleSteps();
				const nextStep = newVisibleSteps[2]; // First step after SITE_NAME
				debugLog(
					"[Wizard] ➡️ nextStep - navigating to:",
					nextStep,
					"visibleSteps:",
					newVisibleSteps,
				);
				this.setState({
					visibleSteps: newVisibleSteps,
					currentStep: nextStep,
					showErrors: false,
				});
			});
			return;
		}

		// Find next step for other steps
		const currentIndex = visibleSteps.indexOf(currentStep);
		const nextStepIndex = currentIndex + 1;

		if (nextStepIndex < visibleSteps.length) {
			const nextStep = visibleSteps[nextStepIndex];
			debugLog("[Wizard] ➡️ nextStep - navigating to:", nextStep);
			this.setState({
				currentStep: nextStep,
				completedSteps: newCompletedSteps,
				showErrors: false,
			});
		}
	};

	prevStep = () => {
		const { currentStep, visibleSteps } = this.state;
		const currentIndex = visibleSteps.indexOf(currentStep);

		if (currentIndex > 0) {
			this.setState({
				currentStep: visibleSteps[currentIndex - 1],
				showErrors: false,
			});
		}
	};

	// Validation
	validateCurrentStep = () => {
		const {
			currentStep,
			templateSource,
			selectedPremadeTemplate,
			selectedExistingSite,
			siteName,
			brandingLogo,
			brandingColor,
			templateDataLoading,
		} = this.state;

		switch (currentStep) {
			case WIZARD_STEPS.TEMPLATE_SELECTION:
				if (!templateSource) return false;
				if (templateSource === "premade" && !selectedPremadeTemplate)
					return false;
				if (templateSource === "existing" && !selectedExistingSite)
					return false;
				return true;
			case WIZARD_STEPS.SITE_NAME:
				// Must have valid site name AND template data must be loaded (for non-scratch)
				if (siteName.trim().length < 2) return false;
				// For scratch builds, no template data needed
				if (templateSource === "scratch") return true;
				// For template-based builds, wait for template data to load
				return !templateDataLoading;
			case WIZARD_STEPS.BRANDING:
				// Both logo and color must be set to proceed
				return brandingLogo && brandingColor;
			default:
				return true;
		}
	};

	// Template selection handlers
	selectTemplateSource = (source) => {
		this.setState({
			templateSource: source,
			selectedPremadeTemplate: null,
			selectedExistingSite: null,
		});
	};

	selectPremadeTemplate = (template) => {
		debugLog("[Wizard] template selected:", template);
		this.setState({
			templateSource: "premade",
			selectedPremadeTemplate: template,
			selectedExistingSite: null, // Clear other selection
			// Reset template data when switching templates (will load on Next)
			templateData: null,
		});
	};

	selectExistingSite = (siteKey) => {
		const site = this.state.existingSites.find((s) => s.Key === siteKey);
		debugLog("[Wizard] site selected:", site);
		this.setState({
			templateSource: "existing",
			selectedExistingSite: site,
			selectedPremadeTemplate: null, // Clear other selection
			// Reset template data when switching templates (will load on Next)
			templateData: null,
		});
	};

	// Handle submission - start job and begin polling
	handleSubmit = async () => {
		this.setState({
			currentStep: WIZARD_STEPS.CREATING,
			creationStep: 0,
			creationError: null,
			jobId: null,
			jobStatus: null,
			jobSteps: [],
			jobOutput: null,
			jobError: null,
		});

		try {
			// Prepare the job input from jobInput state (filters out unselected items)
			const submission = this.prepareJobInputForSubmission();

			if (!submission) {
				throw new Error("No configuration data available");
			}

			debugLog("[Wizard] 🚀 handleSubmit - submitting:", submission);

			// Start the job
			const { data } = await siteConfigActions.startJob(
				"site-creation",
				submission,
			);

			if (!data.success || !data.jobId) {
				throw new Error("Failed to start site creation job");
			}

			this.setState({ jobId: data.jobId });

			// Start polling for job status
			this.startJobPolling(data.jobId);
		} catch (error) {
			console.error("Failed to start site creation:", error);
			this.setState({
				currentStep: WIZARD_STEPS.CONFIRMATION,
				creationError:
					error.message || "Failed to start site creation. Please try again.",
			});
		}
	};

	// Start polling for job status
	startJobPolling = (jobId) => {
		const POLL_INTERVAL_MS = 2000; // Poll every 2 seconds
		const MAX_POLLS = 150; // 5 minutes max
		let pollCount = 0;

		const poll = async () => {
			try {
				const { data } = await siteConfigActions.getJobStatus(jobId);

				debugLog("[Wizard] 📊 Poll response:", {
					status: data.status,
					hasError: !!data.error,
					error: data.error,
				});

				this.setState({
					jobStatus: data.status,
					jobSteps: data.steps || [],
					jobOutput: data.output || null,
					jobError: data.error || null,
				});

				// Update creationStep to match current step index
				const currentStepIndex = data.steps
					? data.steps.findIndex((s) => s.status === "processing")
					: -1;
				if (currentStepIndex >= 0) {
					this.setState({ creationStep: currentStepIndex });
				}

				if (data.status === "complete") {
					// Success!
					if (this.state.pollInterval) {
						clearInterval(this.state.pollInterval);
					}
					this.setState({
						currentStep: WIZARD_STEPS.SUCCESS,
						createdSiteId:
							data.output && data.output.siteId ? data.output.siteId : null,
						pollInterval: null,
					});
					return;
				}

				if (data.status === "failed") {
					// Failed
					debugLog("[Wizard] ❌ Job failed:", {
						error: data.error,
						errorMessage: data.error && data.error.message,
					});
					if (this.state.pollInterval) {
						clearInterval(this.state.pollInterval);
					}
					const errorMessage =
						data.error && data.error.message
							? data.error.message
							: "Site creation failed. Please try again.";
					debugLog("[Wizard] ❌ Setting creationError:", errorMessage);
					this.setState({
						currentStep: WIZARD_STEPS.CONFIRMATION,
						creationError: errorMessage,
						pollInterval: null,
					});
					return;
				}

				// Still processing, continue polling
				pollCount++;
				if (pollCount >= MAX_POLLS) {
					// Timeout
					if (this.state.pollInterval) {
						clearInterval(this.state.pollInterval);
					}
					this.setState({
						currentStep: WIZARD_STEPS.CONFIRMATION,
						creationError:
							"Site creation timed out. Please check the site status and try again if needed.",
						pollInterval: null,
					});
				}
			} catch (error) {
				console.error("Error polling job status:", error);
				// Don't stop polling on transient errors
				pollCount++;
				if (pollCount >= MAX_POLLS) {
					if (this.state.pollInterval) {
						clearInterval(this.state.pollInterval);
					}
					this.setState({
						currentStep: WIZARD_STEPS.CONFIRMATION,
						creationError:
							"Lost connection to server. Please check the site status.",
						pollInterval: null,
					});
				}
			}
		};

		// Start polling
		const interval = setInterval(poll, POLL_INTERVAL_MS);
		this.setState({ pollInterval: interval });

		// Also poll immediately
		poll();
	};

	buildConfiguration = () => {
		// Build the configuration object based on user selections
		const config = {};

		if (this.state.copyFeaturePicker) {
			config.featurePicker = {
				_mode: this.state.linkFeaturePicker ? "linked" : "local",
			};
		}

		if (this.state.copyBranding) {
			config.branding = {
				logo: this.state.brandingLogo,
				mainColor: this.state.brandingColor,
			};
		}

		// Add other config sections...

		return config;
	};

	// Close handler
	handleClose = () => {
		if (this.props.onClose) {
			this.props.onClose();
		} else {
			this.props.history.goBack();
		}
	};

	// Cancel button handler - shows confirmation dialog
	handleCancelClick = () => {
		const { currentStep } = this.state;

		// On template selection step (first step), close immediately without confirmation
		if (currentStep === WIZARD_STEPS.TEMPLATE_SELECTION) {
			this.handleClose();
			return;
		}

		// Show confirmation dialog for all other steps
		this.setState({ showCancelConfirmation: true });
	};

	// Confirm cancel - close the wizard
	confirmCancel = () => {
		this.setState({ showCancelConfirmation: false });
		this.handleClose();
	};

	// Dismiss cancel confirmation dialog
	dismissCancelConfirmation = () => {
		this.setState({ showCancelConfirmation: false });
	};

	// Render cancel confirmation dialog
	renderCancelConfirmation = () => {
		const { showCancelConfirmation } = this.state;

		if (!showCancelConfirmation) {
			return null;
		}

		return (
			<Popup
				title="Discard Changes?"
				onClose={this.dismissCancelConfirmation}
				buttons={[
					{
						type: "primary",
						text: "Continue",
						onClick: this.dismissCancelConfirmation,
						isActive: true,
					},
					{
						type: "tertiary",
						text: "Discard",
						onClick: this.confirmCancel,
						isActive: true,
					},
				]}
				hasPadding
			>
				<Text type="body">
					Are you sure you want to cancel? All progress will be lost.
				</Text>
			</Popup>
		);
	};

	goToNewSite = () => {
		const { createdSiteId } = this.state;
		if (createdSiteId) {
			// Switch to the new site and navigate to its dashboard
			this.props.setAuthLocation(createdSiteId);
			this.props.history.push("/mastermenu");
			// Reload to ensure all site-specific data is refreshed
			setTimeout(() => {
				window.location.reload();
			}, 500);
		} else {
			this.handleClose();
		}
	};

	// Get display title for selected template
	getSelectedTemplateName = () => {
		const { templateSource, selectedPremadeTemplate, selectedExistingSite } =
			this.state;
		if (templateSource === "premade" && selectedPremadeTemplate) {
			return selectedPremadeTemplate.title;
		}
		if (templateSource === "existing" && selectedExistingSite) {
			return selectedExistingSite.Title;
		}
		return "Template";
	};

	/**
	 * Check if linking is available for the current template source.
	 * Linking (inheritance with auto-propagation) is only supported when using
	 * an existing site as template, since cross-account propagation from org
	 * templates is not supported.
	 */
	canLink = () => this.state.templateSource === "existing";

	// Calculate progress percentage (excluding TEMPLATE_SELECTION and SITE_NAME)
	getProgressPercentage = () => {
		const { currentStep, visibleSteps } = this.state;
		// Filter out the first two "setup" steps - progress starts from FEATURE_PICKER
		const progressSteps = visibleSteps.filter(
			(step) =>
				step !== WIZARD_STEPS.TEMPLATE_SELECTION &&
				step !== WIZARD_STEPS.SITE_NAME,
		);
		const currentIndex = progressSteps.indexOf(currentStep);
		if (currentIndex < 0) return 0;
		return Math.round(((currentIndex + 1) / progressSteps.length) * 100);
	};

	// Render sidebar
	renderSidebar = () => {
		const { currentStep, visibleSteps, completedSteps } = this.state;

		// Don't show sidebar during template selection, site name entry, creation, or success
		if (
			currentStep === WIZARD_STEPS.TEMPLATE_SELECTION ||
			currentStep === WIZARD_STEPS.SITE_NAME ||
			currentStep === WIZARD_STEPS.CREATING ||
			currentStep === WIZARD_STEPS.SUCCESS
		) {
			return null;
		}

		const stepLabels = {
			[WIZARD_STEPS.FEATURE_PICKER]: "Feature Picker",
			[WIZARD_STEPS.BRANDING]: "Branding",
			[WIZARD_STEPS.APP_VIEWS]: "App Views",
			[WIZARD_STEPS.USER_TYPES]: "User Types",
			[WIZARD_STEPS.MAINTENANCE_WORKFLOW]: "Maintenance Workflow",
			[WIZARD_STEPS.MAINTENANCE_JOB_TYPES]: "Maintenance Job Types",
			[WIZARD_STEPS.FORMS_WORKFLOW]: "Forms Workflow",
			[WIZARD_STEPS.FORMS]: "Forms",
			[WIZARD_STEPS.CONFIRMATION]: "Confirmation",
		};

		// Filter out TEMPLATE_SELECTION and SITE_NAME from sidebar
		const sidebarSteps = visibleSteps.filter(
			(step) =>
				step !== WIZARD_STEPS.TEMPLATE_SELECTION &&
				step !== WIZARD_STEPS.SITE_NAME,
		);

		return (
			<div className="siteBuilderWizard_sidebar">
				<div className="siteBuilderWizard_sidebar_steps">
					{sidebarSteps.map((step) => {
						const isCompleted = completedSteps.includes(step);
						const isCurrent = currentStep === step;
						const isClickable = isCompleted && !isCurrent;

						return (
							<div
								key={step}
								className={`siteBuilderWizard_sidebar_step ${isCurrent ? "siteBuilderWizard_sidebar_step-active" : ""} ${isCompleted ? "siteBuilderWizard_sidebar_step-completed" : ""} ${isClickable ? "siteBuilderWizard_sidebar_step-clickable" : ""}`}
								onClick={() => isClickable && this.goToStep(step)}
							>
								{isCurrent && (
									<span className="siteBuilderWizard_sidebar_step_bullet">
										•
									</span>
								)}
								<span className="siteBuilderWizard_sidebar_step_label">
									{stepLabels[step] || step}
								</span>
								{isCurrent && (
									<span className="siteBuilderWizard_sidebar_step_arrow">
										▸
									</span>
								)}
							</div>
						);
					})}
				</div>
			</div>
		);
	};

	// Render header
	renderHeader = () => {
		const { currentStep, siteName } = this.state;

		// Don't show header during creation
		if (
			currentStep === WIZARD_STEPS.CREATING ||
			currentStep === WIZARD_STEPS.SUCCESS
		) {
			return null;
		}

		// First two steps (template selection and site name) have "Site Builder" header
		const isInitialStep =
			currentStep === WIZARD_STEPS.TEMPLATE_SELECTION ||
			currentStep === WIZARD_STEPS.SITE_NAME;

		return (
			<div className="siteBuilderWizard_header">
				<Text type="formTitleLarge">
					{isInitialStep
						? "Site Builder"
						: `Building the ${siteName || "New"} site`}
				</Text>
				{!isInitialStep && this.state.templateSource && (
					<Text
						type="formTitleMedium"
						className="siteBuilderWizard_header_template"
					>
						Template: {this.getSelectedTemplateName()}
					</Text>
				)}
			</div>
		);
	};

	// Render progress bar footer
	renderFooter = () => {
		const { currentStep, visibleSteps } = this.state;

		// Don't show footer during creation or success
		if (
			currentStep === WIZARD_STEPS.CREATING ||
			currentStep === WIZARD_STEPS.SUCCESS
		) {
			return null;
		}

		const currentIndex = visibleSteps.indexOf(currentStep);
		const isFirstStep = currentIndex === 0;
		const isLastStep = currentStep === WIZARD_STEPS.CONFIRMATION;
		const isTemplateStep = currentStep === WIZARD_STEPS.TEMPLATE_SELECTION;
		const isSiteNameStep = currentStep === WIZARD_STEPS.SITE_NAME;

		// Show progress bar from feature picker step onwards (after template & site name)
		const showProgressBar = !isTemplateStep && !isSiteNameStep;

		// Show cancel button on all steps (user can cancel until they click "Set Up Site")
		const showCancelButton = true;

		return (
			<div className="siteBuilderWizard_footer">
				{/* Progress bar as thin line on top border - only from feature picker onwards */}
				{showProgressBar && (
					<div
						className="siteBuilderWizard_footer_progressLine"
						style={{ width: `${this.getProgressPercentage()}%` }}
					/>
				)}
				<div className="siteBuilderWizard_footer_buttons">
					{/* Left side: Cancel and Back buttons */}
					<div className="siteBuilderWizard_footer_buttons_left">
						{showCancelButton && (
							<Button
								buttonType="tertiary"
								onClick={this.handleCancelClick}
								isActive
							>
								Cancel
							</Button>
						)}
						{!isFirstStep && (
							<Button buttonType="outlined" onClick={this.prevStep} isActive>
								Back
							</Button>
						)}
					</div>

					{/* Right side: Next or Set Up Site button */}
					<div className="siteBuilderWizard_footer_buttons_right">
						{isLastStep ? (
							<Button
								buttonType="primary"
								onClick={this.handleSubmit}
								isActive={this.validateCurrentStep()}
							>
								Set Up Site
							</Button>
						) : (
							<Button
								buttonType="primary"
								onClick={this.nextStep}
								isActive={this.validateCurrentStep()}
							>
								Next
							</Button>
						)}
					</div>
				</div>
			</div>
		);
	};

	// Render template selection step
	renderTemplateSelection = () => {
		const {
			templateSource,
			selectedPremadeTemplate,
			selectedExistingSite,
			existingSites,
			sitesLoading,
			showErrors,
			orgTemplates,
			orgTemplatesLoading,
			orgTemplatesError,
		} = this.state;

		// Dynamic column count based on template count (keep as inline since it's dynamic)
		const templateCount = orgTemplates.length;
		const columns = templateCount > 0 ? Math.min(templateCount, 4) : 4;

		return (
			<div className="siteBuilderWizard_content_step siteBuilderWizard_content_step-wide">
				<Text type="body" className="marginBottom-16">
					To get started, choose a template for your new site
				</Text>

				{/* Option 1: Pre-made templates */}
				<div className="siteBuilderWizard_section">
					<Text type="formLabel" className="marginBottom-8">
						Option 1: Use a pre-made template
					</Text>

					{/* Loading state */}
					{orgTemplatesLoading && (
						<div className="siteBuilderWizard_loadingState">
							<Text type="help">Loading pre-made templates...</Text>
						</div>
					)}

					{/* Error or empty state */}
					{!orgTemplatesLoading &&
						(orgTemplatesError || orgTemplates.length === 0) && (
							<div className="siteBuilderWizard_emptyState">
								<SVGIcon
									icon="info"
									colour={COLOUR_DUSK_LIGHT}
									width={24}
									height={24}
								/>
								<Text type="help">
									{orgTemplatesError || "No pre-made templates available"}
								</Text>
							</div>
						)}

					{/* Templates grid */}
					{!orgTemplatesLoading && orgTemplates.length > 0 && (
						<div
							className="siteBuilderWizard_templates"
							style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
						>
							{orgTemplates.map((template) => {
								const isSelected =
									templateSource === "premade" &&
									selectedPremadeTemplate &&
									selectedPremadeTemplate.key === template.key;
								return (
									<div
										key={template.key}
										className={`siteBuilderWizard_template ${isSelected ? "siteBuilderWizard_template-selected" : ""}`}
										onClick={() => this.selectPremadeTemplate(template)}
									>
										<div className="siteBuilderWizard_template_icon">
											<SVGIcon
												icon={template.icon}
												colour={COLOUR_BRANDING_ACTION}
												width={32}
												height={32}
											/>
										</div>
										<Text
											type="body"
											className="siteBuilderWizard_template_title"
										>
											{template.title}
										</Text>
									</div>
								);
							})}
						</div>
					)}
				</div>

				{/* Option 2: Existing site dropdown */}
				<div className="siteBuilderWizard_section">
					<Text type="formLabel" className="marginBottom-8">
						Option 2: Use an existing site as a template
					</Text>
					<div
						className={`siteBuilderWizard_dropdownContainer ${selectedExistingSite ? "siteBuilderWizard_dropdownContainer-selected" : ""}`}
					>
						<DropdownInput
							id="existingSite"
							placeholder="Select an existing site..."
							value={selectedExistingSite ? selectedExistingSite.Title : ""}
							options={existingSites}
							onSelect={this.selectExistingSite}
							disabled={sitesLoading}
						/>
					</div>
					{sitesLoading && <Text type="help">Loading sites...</Text>}
				</div>

				{/* Option 3: Start from scratch */}
				<div className="siteBuilderWizard_section">
					<Text type="formLabel" className="marginBottom-8">
						Option 3: Start from scratch
					</Text>
					<div
						className={`siteBuilderWizard_scratchOption ${templateSource === "scratch" ? "siteBuilderWizard_scratchOption-selected" : ""}`}
						onClick={() => this.selectTemplateSource("scratch")}
					>
						<Text type="formLabel">Start From Scratch</Text>
					</div>
				</div>

				{showErrors && !templateSource && (
					<Text type="help" className="siteBuilderWizard_error marginTop-16">
						Please select a template option to continue
					</Text>
				)}
			</div>
		);
	};

	// =========================================================================
	// WIZARD STEP HANDLERS - Update both legacy state and jobInput
	// =========================================================================

	// Handle site name change
	handleSiteNameChange = (e) => {
		const name = e.target.value;
		this.setState({ siteName: name });
		if (this.state.jobInput) {
			this.updateJobInput("name", name);
		}
	};

	// Handle feature picker link toggle - affects both featurePicker and branding modes
	handleLinkFeaturePickerChange = (linked) => {
		this.setState({ linkFeaturePicker: linked });
		if (this.state.jobInput) {
			const mode = linked ? "linked" : "local";
			// Feature picker and branding share the same linking
			this.updateJobInputSection("featurePicker", { _mode: mode });
			this.updateJobInputSection("branding", { _mode: mode });
		}
	};

	// Handle branding logo change
	handleBrandingLogoChange = (url) => {
		console.log("handleBrandingLogoChange", url);
		this.setState({ brandingLogo: url });
		if (this.state.jobInput) {
			this.updateJobInputSection("branding", { logo: url });
		}
	};

	// Handle branding color change
	handleBrandingColorChange = (color) => {
		this.setState({ brandingColor: color });
		if (this.state.jobInput) {
			this.updateJobInputSection("branding", { color: color });
		}
	};

	// Handle user type selection toggle
	handleUserTypeSelectionChange = (userTypeId, selected) => {
		this.setState((prevState) => ({
			copyUserTypes: {
				...prevState.copyUserTypes,
				[userTypeId]: selected,
			},
		}));
		if (this.state.jobInput) {
			this.updateJobInputArrayItem("userTypes", "items", userTypeId, {
				_selected: selected,
			});
		}
	};

	// Handle user type link toggle
	handleUserTypeLinkChange = (userTypeId, linked) => {
		this.setState((prevState) => ({
			linkUserTypes: {
				...prevState.linkUserTypes,
				[userTypeId]: linked,
			},
		}));
		if (this.state.jobInput) {
			this.updateJobInputArrayItem("userTypes", "items", userTypeId, {
				_mode: linked ? "linked" : "local",
			});
		}
	};

	// Handle app view selection toggle
	handleAppViewSelectionChange = (viewId, selected) => {
		this.setState((prevState) => ({
			copyAppViews: {
				...prevState.copyAppViews,
				[viewId]: selected,
			},
		}));
		if (this.state.jobInput) {
			this.updateJobInputArrayItem("appViews", "items", viewId, {
				_selected: selected,
			});
		}
	};

	// Handle app view link toggle
	handleAppViewLinkChange = (viewId, linked) => {
		debugLog("[Wizard] 🔗 handleAppViewLinkChange", { viewId, linked });
		this.setState((prevState) => {
			const newLinkAppViews = {
				...prevState.linkAppViews,
				[viewId]: linked,
			};
			debugLog("[Wizard] 🔗 linkAppViews updated:", newLinkAppViews);
			return { linkAppViews: newLinkAppViews };
		});
		if (this.state.jobInput) {
			this.updateJobInputArrayItem("appViews", "items", viewId, {
				_mode: linked ? "linked" : "local",
			});
		}
	};

	// Handle maintenance workflow link toggle (legacy - individual settings now use direct setState)
	// This function is kept for backwards compatibility but is no longer called by the UI
	handleMaintenanceWorkflowLinkChange = (linked) => {
		// Update both status and hideSeen link states
		this.setState({
			linkStatuses: linked,
			linkHideSeen: linked,
		});
		if (this.state.jobInput) {
			this.updateJobInputSection("maintenance", {
				_mode: linked ? "linked" : "local",
			});
		}
	};

	// Render site name step
	renderSiteNameStep = () => {
		const { siteName, showErrors, templateDataLoading, templateSource } =
			this.state;

		return (
			<div className="siteBuilderWizard_content_step siteBuilderWizard_content_step-fullHeight">
				<Text type="body" className="marginBottom-16">
					Now enter a name for your site
				</Text>

				<div className="siteBuilderWizard_siteNameContainer">
					<input
						type="text"
						className={`siteBuilderWizard_siteNameInput ${showErrors && siteName.trim().length < 2 ? "siteBuilderWizard_siteNameInput-error" : ""}`}
						value={siteName}
						onChange={this.handleSiteNameChange}
						placeholder=""
						autoFocus
					/>
					{showErrors && siteName.trim().length < 2 && (
						<Text
							type="help"
							className="siteBuilderWizard_siteNameError marginTop-8"
						>
							Site name must be at least 2 characters
						</Text>
					)}
					{templateDataLoading && templateSource !== "scratch" && (
						<Text type="help" className="marginTop-16">
							Loading template configuration...
						</Text>
					)}
				</div>
			</div>
		);
	};

	// Render feature picker step
	renderFeaturePickerStep = () => {
		const { copyFeaturePicker, linkFeaturePicker, siteName } = this.state;
		const templateName = this.getSelectedTemplateName();
		const newSiteName = siteName || "New Site";

		return (
			<div className="siteBuilderWizard_content_step">
				<div>
					<RadioButton
						label={`Do you want to copy ${templateName}'s Feature Picker setup?`}
						help="Includes Events, News, Maintenance, Forms, Surveys, People and Groups"
						options={[
							{
								Label: "Yes",
								Value: true,
								onChange: () => this.setState({ copyFeaturePicker: true }),
							},
							{
								Label: "No",
								Value: false,
								onChange: () => this.setState({ copyFeaturePicker: false }),
							},
						]}
						isActive={copyFeaturePicker}
					/>
				</div>

				{copyFeaturePicker && this.canLink() && (
					<div className="marginTop-24">
						<RadioButton
							label={`Do you want to link ${newSiteName}'s Feature Picker to ${templateName}'s Feature Picker setup?`}
							help={`This will disable Feature Picker on ${newSiteName}. Changes to ${templateName} will apply to ${newSiteName}. You can remove this link later.`}
							options={[
								{
									Label: "Yes",
									Value: true,
									onChange: () => this.handleLinkFeaturePickerChange(true),
								},
								{
									Label: "No",
									Value: false,
									onChange: () => this.handleLinkFeaturePickerChange(false),
								},
							]}
							isActive={linkFeaturePicker}
						/>
					</div>
				)}
			</div>
		);
	};

	// Render branding step
	renderBrandingStep = () => {
		const {
			copyBranding,
			brandingLogo,
			brandingColor,
			templateDataLoading,
			templateData,
		} = this.state;
		const templateName = this.getSelectedTemplateName();

		// Get template branding values for pre-filling
		const templateLogo =
			(templateData && templateData.branding && templateData.branding.logo) ||
			null;
		const templateColor =
			(templateData && templateData.branding && templateData.branding.color) ||
			null;

		// Handler for "Yes" - use template branding
		const handleUseTemplateBranding = () => {
			this.setState({
				copyBranding: true,
				brandingLogo: templateLogo,
				brandingColor: templateColor || COLOUR_BRANDING_MAIN,
			});
		};

		// Handler for "No" - customize from scratch
		const handleCustomBranding = () => {
			this.setState({
				copyBranding: false,
				brandingLogo: null,
				brandingColor: null,
			});
		};

		return (
			<div className="siteBuilderWizard_content_step">
				{/* Loading indicator */}
				{templateDataLoading && (
					<Text type="help" className="marginBottom-16">
						Loading template branding...
					</Text>
				)}

				<div>
					<Text type="body" className="marginBottom-10">
						Do you want to use {templateName}'s Branding?
					</Text>
					<RadioButton
						options={[
							{
								Label: "Yes",
								Value: true,
								onChange: handleUseTemplateBranding,
							},
							{
								Label: "No",
								Value: false,
								onChange: handleCustomBranding,
							},
						]}
						isActive={copyBranding}
					/>
				</div>

				{/* Logo and Color inputs are always visible */}
				<div className="siteBuilderWizard_brandingInputs marginTop-24">
					<div className="siteBuilderWizard_brandingRow">
						<div className="siteBuilderWizard_brandingInput">
							<Text type="formLabel" className="marginBottom-8">
								Logo
							</Text>
							{/* Key based on copyBranding to remount only when toggling Yes/No,
							    not on every image upload. hasDefault shows the initial value. */}
							<ImageInput
								key={`branding-logo-${copyBranding}`}
								hasDefault={brandingLogo}
								placeholder="Upload logo"
								refreshCallback={this.handleBrandingLogoChange}
								noCompress
							/>
						</div>
						<div className="siteBuilderWizard_brandingGuidelines">
							<Text
								type="formLabel"
								className="marginBottom-8"
								style={{ color: COLOUR_BRANDING_ACTION }}
							>
								Logo guidelines:
							</Text>
							<Text type="help" className="marginBottom-4">
								• Your logo works best in png format with a transparent
								background.
							</Text>
							<Text type="help">
								• If you use gradient header, a single coloured logo in white
								with a transparent background is recommended.
							</Text>
						</div>
					</div>

					<div className="siteBuilderWizard_brandingInput">
						<Text type="formLabel" className="marginBottom-8">
							Colour
						</Text>
						<ColourPicker
							value={brandingColor || ""}
							onColourSelected={this.handleBrandingColorChange}
							showHex
						>
							<div
								className="colourPicker_button"
								style={{
									width: 80,
									height: 30,
									marginBottom: "unset",
									backgroundColor: brandingColor || "#ccc",
								}}
							/>
						</ColourPicker>
					</div>
				</div>
			</div>
		);
	};

	// Helper to format a list of items for display
	formatItemListSummary = (items, itemType) => {
		if (!items || items.length === 0) return null;
		const names = items.map((item) => item.name);
		if (names.length === 1) {
			return `1 ${itemType}: ${names[0]}`;
		}
		if (names.length === 2) {
			return `2 ${itemType}s: ${names[0]} and ${names[1]}`;
		}
		const lastItem = names.pop();
		return `${names.length + 1} ${itemType}s: ${names.join(", ")} and ${lastItem}`;
	};

	// Render app views step
	renderAppViewsStep = () => {
		const { templateAppViews, copyAppViews, linkAppViews, siteName } =
			this.state;
		const templateName = this.getSelectedTemplateName();
		const newSiteName = siteName || "your new site";

		// Build intro summary text
		const viewsSummary = this.formatItemListSummary(
			templateAppViews,
			"App View",
		);

		return (
			<div className="siteBuilderWizard_content_step">
				{templateAppViews.length === 0 ? (
					<Text type="help">No custom app views found in template.</Text>
				) : (
					<>
						<Text type="body" className="marginBottom-24">
							{templateName} contains {viewsSummary}. You can carry these across
							to the {newSiteName} site.
						</Text>

						{templateAppViews.map((view) => (
							<div key={view.id} className="siteBuilderWizard_itemQuestion">
								<Text type="formLabel" className="marginBottom-8">
									{view.name}
								</Text>
								<div>
									<Text type="body" className="marginBottom-10">
										Do you want to copy {templateName}'s {view.name} App View to{" "}
										{newSiteName}?
									</Text>
									<RadioButton
										options={[
											{
												Label: "Yes",
												Value: true,
												onChange: () =>
													this.handleAppViewSelectionChange(view.id, true),
											},
											{
												Label: "No",
												Value: false,
												onChange: () =>
													this.handleAppViewSelectionChange(view.id, false),
											},
										]}
										isActive={copyAppViews[view.id] !== false}
									/>
								</div>

								{copyAppViews[view.id] !== false && this.canLink() && (
									<div className="marginTop-16">
										<Text type="body" className="marginBottom-10">
											Do you want to link {newSiteName}'s {view.name} App View
											to the same view in {templateName}?
										</Text>
										<RadioButton
											options={[
												{
													Label: "Yes",
													Value: true,
													onChange: () =>
														this.handleAppViewLinkChange(view.id, true),
												},
												{
													Label: "No",
													Value: false,
													onChange: () =>
														this.handleAppViewLinkChange(view.id, false),
												},
											]}
											isActive={linkAppViews[view.id] === true}
										/>
										<Text type="help" className="marginTop-8">
											This will ensure any changes to the {view.name} App View
											in {templateName} will be applied to the {view.name} App
											View in {newSiteName}. You can remove this link later.
										</Text>
									</div>
								)}
							</div>
						))}
					</>
				)}
			</div>
		);
	};

	// Render user types step
	renderUserTypesStep = () => {
		const { templateUserTypes, copyUserTypes, linkUserTypes, siteName } =
			this.state;
		const templateName = this.getSelectedTemplateName();
		const newSiteName = siteName || "your new site";

		// Build intro summary text
		const userTypesSummary = this.formatItemListSummary(
			templateUserTypes,
			"User Type",
		);

		return (
			<div className="siteBuilderWizard_content_step">
				{templateUserTypes.length === 0 ? (
					<Text type="help">No custom user types found in template.</Text>
				) : (
					<>
						<Text type="body" className="marginBottom-24">
							{templateName} contains {userTypesSummary}. You can carry these
							across to the {newSiteName} site.
						</Text>

						{templateUserTypes.map((userType) => (
							<div key={userType.id} className="siteBuilderWizard_itemQuestion">
								<Text type="formLabel" className="marginBottom-8">
									{userType.name}
								</Text>
								<div>
									<Text type="body" className="marginBottom-10">
										Do you want to copy {templateName}'s {userType.name} User
										Type to {newSiteName}?
									</Text>
									<RadioButton
										options={[
											{
												Label: "Yes",
												Value: true,
												onChange: () =>
													this.handleUserTypeSelectionChange(userType.id, true),
											},
											{
												Label: "No",
												Value: false,
												onChange: () =>
													this.handleUserTypeSelectionChange(
														userType.id,
														false,
													),
											},
										]}
										isActive={copyUserTypes[userType.id] !== false}
									/>
								</div>

								{copyUserTypes[userType.id] !== false && this.canLink() && (
									<div className="marginTop-16">
										<Text type="body" className="marginBottom-10">
											Do you want to link {newSiteName}'s {userType.name} User
											Type to the same type in {templateName}?
										</Text>
										<RadioButton
											options={[
												{
													Label: "Yes",
													Value: true,
													onChange: () =>
														this.handleUserTypeLinkChange(userType.id, true),
												},
												{
													Label: "No",
													Value: false,
													onChange: () =>
														this.handleUserTypeLinkChange(userType.id, false),
												},
											]}
											isActive={linkUserTypes[userType.id] === true}
										/>
										<Text type="help" className="marginTop-8">
											This will ensure any changes to the {userType.name} User
											Type in {templateName} will be applied to the{" "}
											{userType.name} User Type in {newSiteName}. You can remove
											this link later.
										</Text>
									</div>
								)}
							</div>
						))}
					</>
				)}
			</div>
		);
	};

	// Render maintenance job types step
	renderMaintenanceJobTypesStep = () => {
		const { templateJobTypes, copyMaintenance, linkMaintenance, siteName } =
			this.state;
		const templateName = this.getSelectedTemplateName();
		const newSiteName = siteName || "your new site";

		// Build intro summary text
		const jobTypesSummary = this.formatItemListSummary(
			templateJobTypes,
			"Job Type",
		);

		return (
			<div className="siteBuilderWizard_content_step">
				{templateJobTypes.length === 0 ? (
					<Text type="help">No job types found in template.</Text>
				) : (
					<>
						<Text type="body" className="marginBottom-24">
							{templateName} contains {jobTypesSummary}. You can carry these
							across to the {newSiteName} site.
						</Text>

						{templateJobTypes.map((jobType) => (
							<div key={jobType.id} className="siteBuilderWizard_itemQuestion">
								<Text type="formLabel" className="marginBottom-8">
									{jobType.name}
								</Text>
								<div>
									<Text type="body" className="marginBottom-10">
										Do you want to copy {templateName}'s {jobType.name} Job Type
										to {newSiteName}?
									</Text>
									<RadioButton
										options={[
											{
												Label: "Yes",
												Value: true,
												onChange: () =>
													this.setState((prev) => ({
														copyMaintenance: {
															...prev.copyMaintenance,
															[jobType.id]: true,
														},
													})),
											},
											{
												Label: "No",
												Value: false,
												onChange: () =>
													this.setState((prev) => ({
														copyMaintenance: {
															...prev.copyMaintenance,
															[jobType.id]: false,
														},
													})),
											},
										]}
										isActive={copyMaintenance[jobType.id] !== false}
									/>
								</div>

								{copyMaintenance[jobType.id] !== false && this.canLink() && (
									<div className="marginTop-16">
										<Text type="body" className="marginBottom-10">
											Do you want to link {newSiteName}'s {jobType.name} Job
											Type to the same type in {templateName}?
										</Text>
										<RadioButton
											options={[
												{
													Label: "Yes",
													Value: true,
													onChange: () =>
														this.setState((prev) => ({
															linkMaintenance: {
																...prev.linkMaintenance,
																[jobType.id]: true,
															},
														})),
												},
												{
													Label: "No",
													Value: false,
													onChange: () =>
														this.setState((prev) => ({
															linkMaintenance: {
																...prev.linkMaintenance,
																[jobType.id]: false,
															},
														})),
												},
											]}
											isActive={linkMaintenance[jobType.id] === true}
										/>
										<Text type="help" className="marginTop-8">
											This will ensure any changes to the {jobType.name} Job
											Type in {templateName} will be applied to the{" "}
											{jobType.name} Job Type in {newSiteName}. You can remove
											this link later.
										</Text>
									</div>
								)}
							</div>
						))}
					</>
				)}
			</div>
		);
	};

	// Render maintenance workflow step
	renderMaintenanceWorkflowStep = () => {
		const {
			templateWorkflow,
			copyStatuses,
			linkStatuses,
			copyHideSeen,
			linkHideSeen,
			siteName,
		} = this.state;
		const templateName = this.getSelectedTemplateName();
		const newSiteName = siteName || "your new site";
		const statuses = templateWorkflow.statuses || [];
		const hideSeen = templateWorkflow.hideSeen;

		return (
			<div className="siteBuilderWizard_content_step">
				{/* ========================================= */}
				{/* Section 1: Statuses */}
				{/* ========================================= */}
				<div className="siteBuilderWizard_itemQuestion">
					<Text type="formLabel" className="marginBottom-10">
						Request Statuses
					</Text>

					{/* Status preview */}
					<div className="marginBottom-16">
						{statuses.length > 0 ? (
							<div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
								{statuses.map((status, index) => (
									<span
										key={index}
										style={{
											display: "inline-block",
											padding: "4px 12px",
											borderRadius: 4,
											backgroundColor: status.color || "#d5dde4",
											color: "#fff",
											fontSize: 12,
											fontWeight: 500,
										}}
									>
										{status.text}
									</span>
								))}
							</div>
						) : (
							<Text type="help">
								Default statuses will be used (Open, In Progress, Completed).
							</Text>
						)}
					</div>

					{/* Copy question */}
					<div>
						<Text type="body" className="marginBottom-10">
							Do you want to copy {templateName}'s statuses to {newSiteName}?
						</Text>
						<RadioButton
							options={[
								{
									Label: "Yes",
									Value: true,
									onChange: () => this.setState({ copyStatuses: true }),
								},
								{
									Label: "No",
									Value: false,
									onChange: () => this.setState({ copyStatuses: false }),
								},
							]}
							isActive={copyStatuses}
						/>
					</div>

					{/* Link question (only if copying and using existing site) */}
					{copyStatuses && this.canLink() && (
						<div className="marginTop-16">
							<Text type="body" className="marginBottom-10">
								Do you want to link {newSiteName}'s statuses to {templateName}?
							</Text>
							<RadioButton
								options={[
									{
										Label: "Yes",
										Value: true,
										onChange: () => this.setState({ linkStatuses: true }),
									},
									{
										Label: "No",
										Value: false,
										onChange: () => this.setState({ linkStatuses: false }),
									},
								]}
								isActive={linkStatuses}
							/>
							<Text type="help" className="marginTop-8">
								Linking means any changes to statuses in {templateName} will be
								applied to {newSiteName}. You can remove this link later.
							</Text>
						</div>
					)}
				</div>

				{/* ========================================= */}
				{/* Section 2: Hide Seen Setting */}
				{/* ========================================= */}
				<div className="siteBuilderWizard_itemQuestion marginTop-32">
					<Text type="formLabel" className="marginBottom-12">
						"Seen" Indicator Setting
					</Text>

					{/* Current setting preview */}
					<div className="marginBottom-16">
						<Text type="help">
							{hideSeen
								? `${templateName} has disabled the "Seen" indicator - users won't see when their request has been viewed.`
								: `${templateName} shows the "Seen" indicator - users can see when their request has been viewed.`}
						</Text>
					</div>

					{/* Copy question */}
					<div>
						<Text type="body" className="marginBottom-10">
							Do you want to copy this setting to {newSiteName}?
						</Text>
						<RadioButton
							options={[
								{
									Label: "Yes",
									Value: true,
									onChange: () => this.setState({ copyHideSeen: true }),
								},
								{
									Label: "No",
									Value: false,
									onChange: () => this.setState({ copyHideSeen: false }),
								},
							]}
							isActive={copyHideSeen}
						/>
					</div>

					{/* Link question (only if copying and using existing site) */}
					{copyHideSeen && this.canLink() && (
						<div className="marginTop-16">
							<Text type="body" className="marginBottom-10">
								Do you want to link this setting to {templateName}?
							</Text>
							<RadioButton
								options={[
									{
										Label: "Yes",
										Value: true,
										onChange: () => this.setState({ linkHideSeen: true }),
									},
									{
										Label: "No",
										Value: false,
										onChange: () => this.setState({ linkHideSeen: false }),
									},
								]}
								isActive={linkHideSeen}
							/>
							<Text type="help" className="marginTop-8">
								Linking means any changes to this setting in {templateName} will
								be applied to {newSiteName}. You can remove this link later.
							</Text>
						</div>
					)}
				</div>
			</div>
		);
	};

	// Render forms step (similar to maintenance job types but for Forms extension)
	renderFormsStep = () => {
		const { templateForms, copyForms, linkForms, siteName } = this.state;
		const templateName = this.getSelectedTemplateName();
		const newSiteName = siteName || "your new site";

		// Build intro summary text
		const formsSummary = this.formatItemListSummary(templateForms, "Form");

		return (
			<div className="siteBuilderWizard_content_step">
				{templateForms.length === 0 ? (
					<Text type="help">No forms found in template.</Text>
				) : (
					<>
						<Text type="body" className="marginBottom-24">
							{templateName} contains {formsSummary}. You can carry these across
							to the {newSiteName} site.
						</Text>

						{templateForms.map((form) => (
							<div key={form.id} className="siteBuilderWizard_itemQuestion">
								<Text type="formLabel" className="marginBottom-8">
									{form.name}
								</Text>
								<div>
									<Text type="body" className="marginBottom-10">
										Do you want to copy {templateName}'s {form.name} Form to{" "}
										{newSiteName}?
									</Text>
									<RadioButton
										options={[
											{
												Label: "Yes",
												Value: true,
												onChange: () =>
													this.setState((prev) => ({
														copyForms: {
															...prev.copyForms,
															[form.id]: true,
														},
													})),
											},
											{
												Label: "No",
												Value: false,
												onChange: () =>
													this.setState((prev) => ({
														copyForms: {
															...prev.copyForms,
															[form.id]: false,
														},
													})),
											},
										]}
										isActive={copyForms[form.id] !== false}
									/>
								</div>

								{copyForms[form.id] !== false && this.canLink() && (
									<div className="marginTop-16">
										<Text type="body" className="marginBottom-10">
											Do you want to link {newSiteName}'s {form.name} Form to
											the same form in {templateName}?
										</Text>
										<RadioButton
											options={[
												{
													Label: "Yes",
													Value: true,
													onChange: () =>
														this.setState((prev) => ({
															linkForms: {
																...prev.linkForms,
																[form.id]: true,
															},
														})),
												},
												{
													Label: "No",
													Value: false,
													onChange: () =>
														this.setState((prev) => ({
															linkForms: {
																...prev.linkForms,
																[form.id]: false,
															},
														})),
												},
											]}
											isActive={linkForms[form.id] === true}
										/>
										<Text type="help" className="marginTop-8">
											This will ensure any changes to the {form.name} Form in{" "}
											{templateName} will be applied to the {form.name} Form in{" "}
											{newSiteName}. You can remove this link later.
										</Text>
									</div>
								)}
							</div>
						))}
					</>
				)}
			</div>
		);
	};

	// Render forms workflow step
	renderFormsWorkflowStep = () => {
		const {
			templateFormsWorkflow,
			copyFormsStatuses,
			linkFormsStatuses,
			copyFormsHideSeen,
			linkFormsHideSeen,
			siteName,
		} = this.state;
		const templateName = this.getSelectedTemplateName();
		const newSiteName = siteName || "your new site";
		const statuses = templateFormsWorkflow.statuses || [];
		const hideSeen = templateFormsWorkflow.hideSeen;

		return (
			<div className="siteBuilderWizard_content_step">
				{/* ========================================= */}
				{/* Section 1: Statuses */}
				{/* ========================================= */}
				<div className="siteBuilderWizard_itemQuestion">
					<Text type="formLabel" className="marginBottom-10">
						Form Request Statuses
					</Text>

					{/* Status preview */}
					<div className="marginBottom-16">
						{statuses.length > 0 ? (
							<div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
								{statuses.map((status, index) => (
									<span
										key={index}
										style={{
											display: "inline-block",
											padding: "4px 12px",
											borderRadius: 4,
											backgroundColor: status.color || "#d5dde4",
											color: "#fff",
											fontSize: 12,
											fontWeight: 500,
										}}
									>
										{status.text}
									</span>
								))}
							</div>
						) : (
							<Text type="help">
								Default statuses will be used (Open, In Progress, Completed).
							</Text>
						)}
					</div>

					{/* Copy question */}
					<div>
						<Text type="body" className="marginBottom-10">
							Do you want to copy {templateName}'s form statuses to{" "}
							{newSiteName}?
						</Text>
						<RadioButton
							options={[
								{
									Label: "Yes",
									Value: true,
									onChange: () => this.setState({ copyFormsStatuses: true }),
								},
								{
									Label: "No",
									Value: false,
									onChange: () => this.setState({ copyFormsStatuses: false }),
								},
							]}
							isActive={copyFormsStatuses}
						/>
					</div>

					{/* Link question (only if copying and using existing site) */}
					{copyFormsStatuses && this.canLink() && (
						<div className="marginTop-16">
							<Text type="body" className="marginBottom-10">
								Do you want to link {newSiteName}'s form statuses to{" "}
								{templateName}?
							</Text>
							<RadioButton
								options={[
									{
										Label: "Yes",
										Value: true,
										onChange: () => this.setState({ linkFormsStatuses: true }),
									},
									{
										Label: "No",
										Value: false,
										onChange: () => this.setState({ linkFormsStatuses: false }),
									},
								]}
								isActive={linkFormsStatuses}
							/>
							<Text type="help" className="marginTop-8">
								Linking means any changes to statuses in {templateName} will be
								applied to {newSiteName}. You can remove this link later.
							</Text>
						</div>
					)}
				</div>

				{/* ========================================= */}
				{/* Section 2: Hide Seen Setting */}
				{/* ========================================= */}
				<div className="siteBuilderWizard_itemQuestion marginTop-32">
					<Text type="formLabel" className="marginBottom-12">
						"Seen" Indicator Setting
					</Text>

					{/* Current setting preview */}
					<div className="marginBottom-16">
						<Text type="help">
							{hideSeen
								? `${templateName} has disabled the "Seen" indicator - users won't see when their form request has been viewed.`
								: `${templateName} shows the "Seen" indicator - users can see when their form request has been viewed.`}
						</Text>
					</div>

					{/* Copy question */}
					<div>
						<Text type="body" className="marginBottom-10">
							Do you want to copy this setting to {newSiteName}?
						</Text>
						<RadioButton
							options={[
								{
									Label: "Yes",
									Value: true,
									onChange: () => this.setState({ copyFormsHideSeen: true }),
								},
								{
									Label: "No",
									Value: false,
									onChange: () => this.setState({ copyFormsHideSeen: false }),
								},
							]}
							isActive={copyFormsHideSeen}
						/>
					</div>

					{/* Link question (only if copying and using existing site) */}
					{copyFormsHideSeen && this.canLink() && (
						<div className="marginTop-16">
							<Text type="body" className="marginBottom-10">
								Do you want to link this setting to {templateName}?
							</Text>
							<RadioButton
								options={[
									{
										Label: "Yes",
										Value: true,
										onChange: () => this.setState({ linkFormsHideSeen: true }),
									},
									{
										Label: "No",
										Value: false,
										onChange: () => this.setState({ linkFormsHideSeen: false }),
									},
								]}
								isActive={linkFormsHideSeen}
							/>
							<Text type="help" className="marginTop-8">
								Linking means any changes to this setting in {templateName} will
								be applied to {newSiteName}. You can remove this link later.
							</Text>
						</div>
					)}
				</div>
			</div>
		);
	};

	// Helper to get summary text for copy/link status
	getSummaryStatus = (copied, linked) => {
		if (!copied) return "Not copied";
		return linked ? "Copied and linked" : "Copied (independent)";
	};

	// Get count of copied items from a copy map
	getCountSummary = (items, copyMap) => {
		if (!items || items.length === 0) return null;
		const copiedCount = items.filter(
			(item) => copyMap[item.id] !== false,
		).length;
		const linkedCount = items.filter(
			(item) => copyMap[item.id] !== false && this.state.linkAppViews[item.id],
		).length;
		if (copiedCount === 0) return "None copied";
		if (linkedCount === copiedCount) return `${copiedCount} copied and linked`;
		if (linkedCount > 0) return `${copiedCount} copied (${linkedCount} linked)`;
		return `${copiedCount} copied (independent)`;
	};

	// Render confirmation step
	renderConfirmationStep = () => {
		const {
			siteName,
			copyFeaturePicker,
			linkFeaturePicker,
			brandingLogo,
			brandingColor,
			copyStatuses,
			linkStatuses,
			copyHideSeen,
			linkHideSeen,
			copyFormsStatuses,
			linkFormsStatuses,
			copyFormsHideSeen,
			linkFormsHideSeen,
			templateAppViews,
			templateUserTypes,
			templateJobTypes,
			templateForms,
			copyAppViews,
			copyUserTypes,
			copyMaintenance,
			copyForms,
			linkAppViews,
			linkUserTypes,
			linkMaintenance,
			linkForms,
			creationError,
		} = this.state;

		const templateName = this.getSelectedTemplateName();

		// Debug logging for confirmation state
		debugLog("[Wizard] 📋 Confirmation state:", {
			brandingLogo,
			brandingColor,
			linkFeaturePicker,
			linkStatuses,
			linkHideSeen,
			linkFormsStatuses,
			linkFormsHideSeen,
			copyAppViews,
			linkAppViews,
			copyUserTypes,
			linkUserTypes,
		});

		// Debug: Log if there's a creation error to display
		if (creationError) {
			debugLog("[Wizard] ⚠️ Creation error present:", creationError);
		}

		// Calculate counts for array items
		const appViewsCopied = templateAppViews.filter(
			(v) => copyAppViews[v.id] !== false,
		);
		const appViewsLinked = appViewsCopied.filter(
			(v) => linkAppViews[v.id] === true,
		);
		debugLog("[Wizard] 📋 App Views counts:", {
			templateAppViews: templateAppViews.map((v) => v.id),
			appViewsCopied: appViewsCopied.map((v) => v.id),
			appViewsLinked: appViewsLinked.map((v) => v.id),
		});
		const userTypesCopied = templateUserTypes.filter(
			(t) => copyUserTypes[t.id] !== false,
		);
		const userTypesLinked = userTypesCopied.filter(
			(t) => linkUserTypes[t.id] === true,
		);
		const jobTypesCopied = templateJobTypes.filter(
			(j) => copyMaintenance[j.id] !== false,
		);
		const jobTypesLinked = jobTypesCopied.filter(
			(j) => linkMaintenance[j.id] === true,
		);
		const formTypesCopied = (templateForms || []).filter(
			(f) => copyForms[f.id] !== false,
		);
		const formTypesLinked = formTypesCopied.filter(
			(f) => linkForms[f.id] === true,
		);

		return (
			<div className="siteBuilderWizard_content_step siteBuilderWizard_content_step-centered">
				{creationError && (
					<div className="siteBuilderWizard_error siteBuilderWizard_error-box marginBottom-24">
						<SVGIcon icon="warning" colour="#e74c3c" width={20} height={20} />
						<Text type="error">{creationError}</Text>
					</div>
				)}

				<div className="siteBuilderWizard_summary">
					<div className="siteBuilderWizard_summary_item">
						<Text type="formLabel">Site Name</Text>
						<Text type="body">{siteName}</Text>
					</div>

					<div className="siteBuilderWizard_summary_item">
						<Text type="formLabel">Template</Text>
						<Text type="body">{templateName}</Text>
					</div>

					<div className="siteBuilderWizard_summary_item">
						<Text type="formLabel">Feature Picker</Text>
						<Text type="body">
							{this.getSummaryStatus(copyFeaturePicker, linkFeaturePicker)}
						</Text>
					</div>

					<div className="siteBuilderWizard_summary_item">
						<Text type="formLabel">Branding</Text>
						{brandingLogo || brandingColor ? (
							<div
								style={{
									marginTop: 4,
									display: "flex",
									alignItems: "center",
									gap: 12,
								}}
							>
								{brandingLogo && (
									<img
										src={brandingLogo}
										alt="Logo"
										style={{ height: 24, maxWidth: 80, objectFit: "contain" }}
									/>
								)}
								{brandingColor && (
									<div
										style={{ display: "flex", alignItems: "center", gap: 4 }}
									>
										<div
											style={{
												width: 16,
												height: 16,
												borderRadius: 2,
												backgroundColor: brandingColor,
												border: "1px solid #ccc",
											}}
										/>
										<Text type="body">{brandingColor}</Text>
									</div>
								)}
							</div>
						) : (
							<Text type="body">No branding configured</Text>
						)}
					</div>

					{templateAppViews.length > 0 && (
						<div className="siteBuilderWizard_summary_item">
							<Text type="formLabel">App Views</Text>
							<Text type="body">
								{appViewsCopied.length === 0
									? "None copied"
									: appViewsLinked.length === appViewsCopied.length
										? `${appViewsCopied.length} copied and linked`
										: appViewsLinked.length > 0
											? `${appViewsCopied.length} copied (${appViewsLinked.length} linked)`
											: `${appViewsCopied.length} copied (independent)`}
							</Text>
						</div>
					)}

					{templateUserTypes.length > 0 && (
						<div className="siteBuilderWizard_summary_item">
							<Text type="formLabel">User Types</Text>
							<Text type="body">
								{userTypesCopied.length === 0
									? "None copied"
									: userTypesLinked.length === userTypesCopied.length
										? `${userTypesCopied.length} copied and linked`
										: userTypesLinked.length > 0
											? `${userTypesCopied.length} copied (${userTypesLinked.length} linked)`
											: `${userTypesCopied.length} copied (independent)`}
							</Text>
						</div>
					)}

					<div className="siteBuilderWizard_summary_item">
						<Text type="formLabel">Maintenance Workflow</Text>
						<Text type="body">
							Statuses: {this.getSummaryStatus(copyStatuses, linkStatuses)}
							<br />
							"Seen" Setting:{" "}
							{this.getSummaryStatus(copyHideSeen, linkHideSeen)}
						</Text>
					</div>

					{templateJobTypes.length > 0 && (
						<div className="siteBuilderWizard_summary_item">
							<Text type="formLabel">Maintenance Job Types</Text>
							<Text type="body">
								{jobTypesCopied.length === 0
									? "None copied"
									: jobTypesLinked.length === jobTypesCopied.length
										? `${jobTypesCopied.length} copied and linked`
										: jobTypesLinked.length > 0
											? `${jobTypesCopied.length} copied (${jobTypesLinked.length} linked)`
											: `${jobTypesCopied.length} copied (independent)`}
							</Text>
						</div>
					)}

					<div className="siteBuilderWizard_summary_item">
						<Text type="formLabel">Forms Workflow</Text>
						<Text type="body">
							Statuses:{" "}
							{this.getSummaryStatus(copyFormsStatuses, linkFormsStatuses)}
							<br />
							"Seen" Setting:{" "}
							{this.getSummaryStatus(copyFormsHideSeen, linkFormsHideSeen)}
						</Text>
					</div>

					{(templateForms || []).length > 0 && (
						<div className="siteBuilderWizard_summary_item">
							<Text type="formLabel">Forms</Text>
							<Text type="body">
								{formTypesCopied.length === 0
									? "None copied"
									: formTypesLinked.length === formTypesCopied.length
										? `${formTypesCopied.length} copied and linked`
										: formTypesLinked.length > 0
											? `${formTypesCopied.length} copied (${formTypesLinked.length} linked)`
											: `${formTypesCopied.length} copied (independent)`}
							</Text>
						</div>
					)}
				</div>
			</div>
		);
	};

	// Render creation progress - simple spinner while site is being created
	renderCreatingStep = () => {
		const { lottieOptions } = this.state;

		return (
			<div className="siteBuilderWizard_creating">
				{lottieOptions.animationData && (
					<Lottie
						key="creation-lottie"
						height={300}
						width={500}
						options={lottieOptions}
					/>
				)}
				<Text type="formTitleLarge" className="marginTop-24">
					Creating your site...
				</Text>
				<Text type="help" className="marginTop-8" style={{ color: "#666" }}>
					This usually takes just a few seconds
				</Text>
				<div className="siteBuilderWizard_creating_spinner marginTop-24">
					<div className="siteBuilderWizard_creating_step_spinner" />
				</div>
			</div>
		);
	};

	// Render success step
	renderSuccessStep = () => {
		const { siteName } = this.state;

		return (
			<div className="siteBuilderWizard_success">
				<div className="siteBuilderWizard_success_icon">
					<SVGIcon icon="checkmark" colour="#fff" width={48} height={48} />
				</div>
				<Text type="formTitleLarge" className="marginTop-24">
					Site Created Successfully!
				</Text>
				<Text type="body" className="marginTop-16 marginBottom-32">
					Your new site "{siteName}" has been created and configured.
				</Text>
				<Button buttonType="primary" onClick={this.goToNewSite} isActive>
					Go to {siteName}
				</Button>
			</div>
		);
	};

	// Render current step content
	renderCurrentStepContent = () => {
		const { currentStep } = this.state;

		switch (currentStep) {
			case WIZARD_STEPS.TEMPLATE_SELECTION:
				return this.renderTemplateSelection();
			case WIZARD_STEPS.SITE_NAME:
				return this.renderSiteNameStep();
			case WIZARD_STEPS.FEATURE_PICKER:
				return this.renderFeaturePickerStep();
			case WIZARD_STEPS.BRANDING:
				return this.renderBrandingStep();
			case WIZARD_STEPS.APP_VIEWS:
				return this.renderAppViewsStep();
			case WIZARD_STEPS.USER_TYPES:
				return this.renderUserTypesStep();
			case WIZARD_STEPS.MAINTENANCE_WORKFLOW:
				return this.renderMaintenanceWorkflowStep();
			case WIZARD_STEPS.MAINTENANCE_JOB_TYPES:
				return this.renderMaintenanceJobTypesStep();
			case WIZARD_STEPS.FORMS_WORKFLOW:
				return this.renderFormsWorkflowStep();
			case WIZARD_STEPS.FORMS:
				return this.renderFormsStep();
			case WIZARD_STEPS.CONFIRMATION:
				return this.renderConfirmationStep();
			case WIZARD_STEPS.CREATING:
				return this.renderCreatingStep();
			case WIZARD_STEPS.SUCCESS:
				return this.renderSuccessStep();
			default:
				return null;
		}
	};

	render() {
		const { currentStep } = this.state;
		// Full screen mode hides the sidebar for these steps
		const isFullScreen =
			currentStep === WIZARD_STEPS.TEMPLATE_SELECTION ||
			currentStep === WIZARD_STEPS.SITE_NAME ||
			currentStep === WIZARD_STEPS.CONFIRMATION ||
			currentStep === WIZARD_STEPS.CREATING ||
			currentStep === WIZARD_STEPS.SUCCESS;
		const isOverflow = currentStep === WIZARD_STEPS.TEMPLATE_SELECTION;

		return (
			<>
				<Popup
					className="siteBuilderWizard_popup"
					boxClasses="siteBuilderWizard_popup_box"
					innerClasses="siteBuilderWizard_popup_inner"
					minWidth={isFullScreen ? "800px" : "1000px"}
					minHeight={isFullScreen ? "500px" : "700px"}
					maxHeight="700px"
				>
					<div
						className={`siteBuilderWizard ${isFullScreen ? "siteBuilderWizard-fullscreen" : ""}`}
					>
						{this.renderHeader()}
						<div className="siteBuilderWizard_body">
							{!isFullScreen && this.renderSidebar()}
							<div
								className={`siteBuilderWizard_content${isOverflow ? " siteBuilderWizard_content-overflow" : ""}`}
							>
								{this.renderCurrentStepContent()}
							</div>
						</div>
						{this.renderFooter()}
					</div>
				</Popup>
				{this.renderCancelConfirmation()}
			</>
		);
	}
}

const mapStateToProps = (state) => {
	return {
		auth: state.auth,
	};
};

const mapDispatchToProps = {
	setAuthLocation,
};

export default connect(
	mapStateToProps,
	mapDispatchToProps,
)(withRouter(SiteBuilderWizard));
